import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["children"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { useIntl } from 'react-intl-next';
import { CellMeasurerCache } from 'react-virtualized/dist/commonjs/CellMeasurer';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { ELEMENT_ITEM_HEIGHT, ElementBrowser } from '@atlaskit/editor-common/element-browser';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { messages, IconCode, IconDate, IconDecision, IconDivider, IconExpand, IconPanel, IconQuote, IconStatus } from '@atlaskit/editor-common/quick-insert';
import { OutsideClickTargetRefContext, withReactEditorViewOuterListeners as withOuterListeners } from '@atlaskit/editor-common/ui-react';
import { fg } from '@atlaskit/platform-feature-flags';
import { N0, N30A, N60A } from '@atlaskit/theme/colors';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expVal } from '@atlaskit/tmp-editor-statsig/expVal';
export var DEFAULT_HEIGHT = 560;

/**
 * Exported helper to allow testing of InsertMenu whiteboard pinning logic. NOTE: this is
   *not* the ideal way to approach this, quickinsert plugin provides a `getSuggestions`
   method that can be used to get suggestions -> once all experiments are cleaned up,
   they should be unified through `pluginInjectionApi?.quickInsert?.actions.getSuggestions`
 */
export var filterForPinWhiteboardsExperiment = function filterForPinWhiteboardsExperiment(featuredItems, formatMessage) {
  // Legacy path fallback -- prior comments as follows:
  /**
   * // Part of ATLAS-95399 to pin whiteboards to the top of the InsertMenu
  	// Need to check if whiteboard options are available, and filter for the cohort
  	// Takes the original featuredItems list and returns one with the right whiteboard option at the top
   */
  if (fg('confluence-whiteboards-quick-insert-eligible')) {
    var DIAGRAM_TITLE = 'Create diagram',
      BLANK_TITLE = 'Create whiteboard';
    var featuredWhiteboardsPresent = featuredItems.filter(function (item) {
      return [DIAGRAM_TITLE, BLANK_TITLE].includes(item.title);
    }).length === 2;
    if (featuredWhiteboardsPresent) {
      var pinWhiteboardActionToTop = function pinWhiteboardActionToTop(items, title) {
        // find the requested item by title, give it the appropriate description, and bring it to the top of the list
        var index = items.findIndex(function (item) {
          return item.title === title;
        });
        var filteredList = items.filter(function (item) {
          return ![DIAGRAM_TITLE, BLANK_TITLE].includes(item.title);
        });
        if (index === -1) {
          return filteredList;
        }
        var featuredItem = _objectSpread({}, items[index]);
        featuredItem.description = formatMessage(messages.featuredWhiteboardDescription);
        return [featuredItem].concat(_toConsumableArray(filteredList));
      };
      if (expValEquals('confluence_whiteboards_quick_insert', 'cohort', 'test_blank')) {
        return pinWhiteboardActionToTop(featuredItems, BLANK_TITLE);
      }
      if (expValEquals('confluence_whiteboards_quick_insert', 'cohort', 'test_diagram')) {
        return pinWhiteboardActionToTop(featuredItems, DIAGRAM_TITLE);
      }
      // NOTE this is not desirable/the OG behaviour, but given we've shipped the test_diagram variant,
      return featuredItems.filter(function (item) {
        return ![DIAGRAM_TITLE, BLANK_TITLE].includes(item.title);
      });
    } else {
      if (fg('confluence-whiteboards-quick-insert-l10n-eligible')) {
        // Fire exposure for confluence_whiteboards_quick_insert_localised_aa
        // https://switcheroo.atlassian.com/ui/gates/ccd80d32-28a1-4dcf-b3f9-dbdc02a046ff/key/confluence_whiteboards_quick_insert_localised_aa
        expVal('confluence_whiteboards_quick_insert_localised_aa', 'cohort', 'test_diagram');

        /** BEGIN locale agnostic path */

        /**
         * EXTREMELY IMPORTANT: we must not drop diagram for those who already receive
         * the 'insert diagram to the top' treatment.
         *
         * Our heuristic to check that this is only targeting users where they haven't
         * gotten the experience, is if we _cannot_ find the blank board experience in
         * the list, matching purely on title.
         *
         * e.g. `featuredWhiteboardsPresent` = false, given it matches on title.
         *
         * The side-effect of this, is that there's a small chance/edge case of users
         * who toggle between locales, and receive different experiences.
         *
         * Hopefully we can make a call early on this experiment, and eliminate this
         * code path.
         */
        var WHITEBOARD_KEY = 'whiteboard-extension:create-whiteboard';
        var DIAGRAM_KEY = 'whiteboard-extension:create-diagram';
        var isBlank = function isBlank(item) {
          return item.key === WHITEBOARD_KEY;
        };
        var isDiagram = function isDiagram(item) {
          return item.key === DIAGRAM_KEY;
        };
        var hasBoth = featuredItems.some(isBlank) && featuredItems.some(isDiagram);
        if (hasBoth) {
          var pin = function pin(key) {
            var idx = featuredItems.findIndex(function (item) {
              return item.key === key;
            });
            var filtered = featuredItems.filter(function (item) {
              return !isBlank(item) && !isDiagram(item);
            });
            if (idx === -1) {
              return filtered;
            }
            var picked = _objectSpread(_objectSpread({}, featuredItems[idx]), {}, {
              description: formatMessage(messages.featuredWhiteboardDescription)
            });
            return [picked].concat(_toConsumableArray(filtered));
          };
          if (expValEquals('confluence_whiteboards_quick_insert_localised', 'cohort', 'test_blank')) {
            return pin(WHITEBOARD_KEY);
          }
          if (expValEquals('confluence_whiteboards_quick_insert_localised', 'cohort', 'test_diagram')) {
            return pin(DIAGRAM_KEY);
          }
          if (expValEquals('confluence_whiteboards_quick_insert_localised', 'cohort', 'control')) {
            return featuredItems;
          }
        }
      }
      /** END locale agnostic path */
    }
  }
  return featuredItems;
};
var selector = function selector(states) {
  var _states$connectivityS;
  return {
    connectivityMode: (_states$connectivityS = states.connectivityState) === null || _states$connectivityS === void 0 ? void 0 : _states$connectivityS.mode
  };
};
var InsertMenu = function InsertMenu(_ref) {
  var _pluginInjectionApi$q6, _pluginInjectionApi$q7;
  var editorView = _ref.editorView,
    dropdownItems = _ref.dropdownItems,
    showElementBrowserLink = _ref.showElementBrowserLink,
    onInsert = _ref.onInsert,
    toggleVisiblity = _ref.toggleVisiblity,
    pluginInjectionApi = _ref.pluginInjectionApi,
    isFullPageAppearance = _ref.isFullPageAppearance;
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    itemCount = _useState2[0],
    setItemCount = _useState2[1];
  var _useState3 = useState(DEFAULT_HEIGHT),
    _useState4 = _slicedToArray(_useState3, 2),
    height = _useState4[0],
    setHeight = _useState4[1];
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var cache = useMemo(function () {
    return new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: ELEMENT_ITEM_HEIGHT,
      minHeight: ELEMENT_ITEM_HEIGHT
    });
  }, []);
  useLayoutEffect(function () {
    // Figure based on visuals to exclude the searchbar, padding/margin, and the ViewMore item.
    var EXTRA_SPACE_EXCLUDING_ELEMENTLIST = 128;
    var totalItemHeight = _toConsumableArray(Array(itemCount)).reduce(function (sum, _, index) {
      return sum + cache.rowHeight({
        index: index
      });
    }, 0) + EXTRA_SPACE_EXCLUDING_ELEMENTLIST;
    if (itemCount > 0 && totalItemHeight < DEFAULT_HEIGHT) {
      setHeight(totalItemHeight);
    } else {
      setHeight(DEFAULT_HEIGHT);
    }
  }, [cache, itemCount]);
  var transform = useCallback(function (item) {
    return {
      title: item.content,
      description: item.tooltipDescription,
      keyshortcut: item.shortcut,
      icon: function icon() {
        return getSvgIconForItem({
          name: item.value.name
        }) || item.elemBefore;
      },
      /**
       * @note This transformed items action is only used when a quick insert item has been
       * called from the quick insert menu and a search has not been performed.
       */
      action: function action() {
        return onInsert({
          item: item
        });
      },
      // "insertInsertMenuItem" expects these 2 properties.
      onClick: item.onClick,
      value: item.value
    };
  }, [onInsert]);
  var quickInsertDropdownItems = dropdownItems.map(transform);

  // Please clean up viewMoreItem when cleaning up platform_editor_refactor_view_more
  var viewMoreItem = !fg('platform_editor_refactor_view_more') && showElementBrowserLink ? quickInsertDropdownItems.pop() : undefined;
  var onInsertItem = useCallback(function (item) {
    var _pluginInjectionApi$q;
    toggleVisiblity();
    if (!editorView.hasFocus()) {
      editorView.focus();
    }
    pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$q = pluginInjectionApi.quickInsert) === null || _pluginInjectionApi$q === void 0 || _pluginInjectionApi$q.actions.insertItem(item, INPUT_METHOD.TOOLBAR)(editorView.state, editorView.dispatch);
  }, [editorView, toggleVisiblity, pluginInjectionApi]);
  var _useSharedPluginState = useSharedPluginStateWithSelector(pluginInjectionApi, ['connectivity'], selector),
    connectivityMode = _useSharedPluginState.connectivityMode;
  var getItems = useCallback(function (query, category) {
    var result;
    /**
     * @warning The results if there is a query are not the same as the results if there is no query.
     * For example: If you have a typed panel and then select the panel item then it will call a different action
     * than is specified on the editor plugins quick insert
     * @see above transform function for more details.
     */
    if (query) {
      var _pluginInjectionApi$q2, _pluginInjectionApi$q3;
      result = (_pluginInjectionApi$q2 = pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$q3 = pluginInjectionApi.quickInsert) === null || _pluginInjectionApi$q3 === void 0 || (_pluginInjectionApi$q3 = _pluginInjectionApi$q3.actions.getSuggestions({
        query: query,
        category: category
      })) === null || _pluginInjectionApi$q3 === void 0 ? void 0 : _pluginInjectionApi$q3.map(function (item) {
        return connectivityMode === 'offline' && item.isDisabledOffline ? _objectSpread(_objectSpread({}, item), {}, {
          isDisabled: true
        }) : item;
      })) !== null && _pluginInjectionApi$q2 !== void 0 ? _pluginInjectionApi$q2 : [];
    } else {
      var _pluginInjectionApi$q4, _pluginInjectionApi$q5;
      var featuredQuickInsertSuggestions = (_pluginInjectionApi$q4 = pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$q5 = pluginInjectionApi.quickInsert) === null || _pluginInjectionApi$q5 === void 0 || (_pluginInjectionApi$q5 = _pluginInjectionApi$q5.actions.getSuggestions({
        category: category,
        featuredItems: true
      })) === null || _pluginInjectionApi$q5 === void 0 ? void 0 : _pluginInjectionApi$q5.map(function (item) {
        return connectivityMode === 'offline' && item.isDisabledOffline ? _objectSpread(_objectSpread({}, item), {}, {
          isDisabled: true
        }) : item;
      })) !== null && _pluginInjectionApi$q4 !== void 0 ? _pluginInjectionApi$q4 : [];
      var unfilteredResult = quickInsertDropdownItems.concat(featuredQuickInsertSuggestions);
      // need to filter on the concatenated list so whiteboards are at the top
      result = filterForPinWhiteboardsExperiment(unfilteredResult, formatMessage);
    }
    setItemCount(result.length);
    return result;
  }, [pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$q6 = pluginInjectionApi.quickInsert) === null || _pluginInjectionApi$q6 === void 0 ? void 0 : _pluginInjectionApi$q6.actions, quickInsertDropdownItems, connectivityMode, formatMessage]);
  var emptyStateHandler = pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$q7 = pluginInjectionApi.quickInsert) === null || _pluginInjectionApi$q7 === void 0 || (_pluginInjectionApi$q7 = _pluginInjectionApi$q7.sharedState.currentState()) === null || _pluginInjectionApi$q7 === void 0 ? void 0 : _pluginInjectionApi$q7.emptyStateHandler;
  var onViewMore = useCallback(function () {
    var _pluginInjectionApi$c, _pluginInjectionApi$q8;
    toggleVisiblity();
    pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$c = pluginInjectionApi.core) === null || _pluginInjectionApi$c === void 0 || _pluginInjectionApi$c.actions.execute(pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$q8 = pluginInjectionApi.quickInsert) === null || _pluginInjectionApi$q8 === void 0 ? void 0 : _pluginInjectionApi$q8.commands.openElementBrowserModal);
  }, [pluginInjectionApi, toggleVisiblity]);
  return (
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
    jsx("div", {
      css: insertMenuWrapper(height, isFullPageAppearance)
    }, jsx(ElementBrowserWrapper, {
      handleClickOutside: toggleVisiblity,
      handleEscapeKeydown: toggleVisiblity,
      closeOnTab: false
    }, jsx(ElementBrowser, {
      mode: "inline",
      getItems: getItems,
      emptyStateHandler: emptyStateHandler,
      onInsertItem: onInsertItem,
      showSearch: true,
      showCategories: false
      // On page resize we want the InlineElementBrowser to show updated tools/overflow items
      ,
      key: quickInsertDropdownItems.length,
      viewMoreItem: viewMoreItem,
      onViewMore: showElementBrowserLink ? onViewMore : undefined,
      cache: cache
    })))
  );
};
var getSvgIconForItem = function getSvgIconForItem(_ref2) {
  var name = _ref2.name;
  var Icon = {
    codeblock: IconCode,
    panel: IconPanel,
    blockquote: IconQuote,
    decision: IconDecision,
    horizontalrule: IconDivider,
    expand: IconExpand,
    date: IconDate,
    status: IconStatus
  }[name];
  return Icon ? jsx(Icon, {
    label: ""
  }) : undefined;
};
var insertMenuWrapper = function insertMenuWrapper(height, isFullPageAppearance) {
  return css({
    display: 'flex',
    flexDirection: 'column',
    width: '320px',
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    height: "".concat(height, "px"),
    backgroundColor: "".concat("var(--ds-surface-overlay, ".concat(N0, ")")),
    borderRadius: "var(--ds-radius-small, 3px)",
    boxShadow: "".concat("var(--ds-shadow-overlay, ".concat("0 0 0 1px ".concat(N30A, ",\n    0 2px 1px ").concat(N30A, ",\n    0 0 20px -6px ").concat(N60A), ")"))
  });
};
var flexWrapperStyles = css({
  display: 'flex',
  flex: 1,
  boxSizing: 'border-box',
  overflow: 'hidden'
});
var FlexWrapper = function FlexWrapper(props) {
  var setOutsideClickTargetRef = useContext(OutsideClickTargetRefContext);
  var children = props.children,
    divProps = _objectWithoutProperties(props, _excluded);
  return (
    // Ignored via go/ees005
    // eslint-disable-next-line react/jsx-props-no-spreading
    jsx("div", _extends({
      ref: setOutsideClickTargetRef,
      css: flexWrapperStyles
    }, divProps), children)
  );
};
var ElementBrowserWrapper = withOuterListeners(FlexWrapper);
export default InsertMenu;